import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { PaymentToast } from "@/core/state/paymentToasts";
import { lancerVente } from "@/shell/fete/moteur";

/**
 * 💚 LA FÊTE DE LA VENTE — au milieu de l'écran, chez le VENDEUR.
 *
 * Kayla, 19-09-2026, puis 09-10-2026 : la pop-up verte est devenue une scène
 * (cf `fete/moteur.ts`) — un objet par palier de montant, comme au bowling,
 * son impact, ses sons, et dessous le fan et « Ouvrir la discussion ». Les
 * ventes des autres restent des petits toasts en bas à droite.
 *
 * Ce composant ne fait que monter la scène quand une vente arrive et la
 * démonter quand elle part : tout le spectacle vit dans le moteur. Ses sons ne
 * jouent que dans la fenêtre qui a gagné le son (`toast.sonIci`).
 */

const SYMBOLE: Record<string, string> = { EUR: "€", USD: "$", GBP: "£" };

export function VenteCelebration({
  toast,
  onDismiss,
  onOpen,
}: {
  /** `null` = rien à l'écran. Chaque nouvelle vente relance la scène. */
  toast: PaymentToast | null;
  onDismiss: () => void;
  /** Ouvre la conversation du fan ; `null` quand l'événement ne la porte pas. */
  onOpen: (() => void) | null;
}) {
  const { t } = useTranslation();
  const racine = useRef<HTMLDivElement>(null);
  // Les rappels changent à chaque rendu du parent : la scène lit toujours les
  // derniers, sans redémarrer pour autant.
  const rappels = useRef({ onDismiss, onOpen });
  rappels.current = { onDismiss, onOpen };

  useEffect(() => {
    if (!toast || !racine.current) return;
    const devise = SYMBOLE[toast.currency] ?? toast.currency;
    const montant = (cents: number) => `${Number.isInteger(cents / 100) ? cents / 100 : (cents / 100).toFixed(2)} ${devise}`;
    return lancerVente(
      racine.current,
      {
        euros: toast.amountCents / 100,
        devise,
        fan: toast.fanName,
        createur: toast.creatorName,
        quoi: toast.itemSold,
        combo: toast.combo,
        venteDuJour: toast.venteDuJour,
        totalDuJour: toast.totalDuJourCents === null ? null : montant(toast.totalDuJourCents),
        peutOuvrir: toast.conversationId !== null,
      },
      toast.sonIci,
      {
        ouvrir: t("shell.venteCelebration.open", { defaultValue: "Ouvrir la discussion" }),
        fermer: t("common.close", { defaultValue: "Fermer" }),
        aQui: (name) => t("shell.venteCelebration.to", { name, defaultValue: "à {{name}}" }),
        venteDuJour: (n) => t("shell.venteCelebration.venteDuJour", { n, defaultValue: "Vente n°{{n}} aujourd'hui" }),
        totalDuJour: (m) => t("shell.venteCelebration.totalDuJour", { montant: m, defaultValue: "{{montant}} aujourd'hui" }),
      },
      {
        onOuvrir: () => rappels.current.onOpen?.(),
        onFermer: () => rappels.current.onDismiss(),
      },
    );
  }, [toast, t]);

  if (!toast) return null;
  return <div ref={racine} data-testid="vente-celebration" role="status" aria-live="polite" />;
}
